import React from 'react';
import { Button, Icon, Message, Modal, InputMulti, SelectHierarchyLabel } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Tag from '../../../../models/tag';
import EventModel from '../../../../models/eventModel';
import Style from '../eventCard.module.css';

export default function TagModal({ open, onClose, tags, event, organization }) {
    const { t } = useTranslation();
    const [createMode, setCreateMode] = React.useState(false);
    const [tagList, setTagList] = React.useState([]);
    const [selected, setSelected] = React.useState(event?.tags.map(tag => tag.id) || []);

    const onCloseTagModal = React.useCallback(() => {
        onClose();
        setCreateMode(false);
    }, [onClose]);

    const addTags = React.useCallback(
        promises => {
            const tagsIds = event?.tags.map(tag => tag.id);
            let tags_to_add = tagsIds.concat(promises).filter((x, i, a) => a.indexOf(x) === i);
            let eventObject = new EventModel({
                id: event?.id,
                tags: tags_to_add
            });
            eventObject.save({ organizationId: organization.id });
        },
        [event, organization]
    );

    const createTags = React.useCallback(() => {
        const newTag = new Tag();
        const organizationId = organization.id;
        let tags_already_created = event?.tags.map(tag => tag.label);
        let tag_to_add = tagList
            .filter(tag => !tags_already_created.includes(tag))
            .map(tag => {
                newTag['label'] = tag;
                return newTag
                    .save({ organizationId })
                    .then(({ response }) => {
                        return response.id;
                    })
                    .catch(e => {
                        console.log(e);
                    });
            });

        Promise.all(tag_to_add)
            .then(promises => {
                addTags(promises);
            })
            .catch(e => console.log(e))
            .finally(() => {
                onCloseTagModal();
            });
    }, [tagList, organization, event, onCloseTagModal]);

    const options = React.useMemo(() => {
        return (
            tags &&
            tags.map(tag => {
                return { text: tag.label, value: tag.id };
            })
        );
    }, [tags]);

    const handleChange = React.useCallback(
        selected => {
            let selectedTmp = !Array.isArray(selected) ? [selected] : selected;
            setSelected(selectedTmp);
        },
        [setSelected]
    );

    const handleLabelChange = selected => {
        return selected.length ? t('eventList.tagModal.selectedTag', { count: selected.length }) : '';
    };
    const selectedTags = React.useMemo(() => {
        const tag = [];
        selected.forEach(id => {
            tags.forEach(t => {
                if (id === t.id) {
                    tag.push({ id: t.id, label: t.label });
                }
            });
        });
        return tag;
    }, [tags, selected]);

    const goBackToSelectTags = React.useCallback(() => {
        setCreateMode(false);
        setSelected(event?.tags.map(tag => tag.id));
    }, [event]);

    const goCreateTags = React.useCallback(() => {
        setCreateMode(true);
        setTagList([]);
    });

    const onSubmit = React.useCallback(() => {
        !createMode ? addTags(selectedTags.map(selected => selected.id)) : createTags();
        onCloseTagModal();
        setSelected(event?.tags.map(tag => tag.id));
    }, [selectedTags, createTags, addTags, onCloseTagModal, createMode]);

    return (
        <Modal
            allowExternalClosing={false}
            open={open}
            onClose={onClose}
            submitButton={<Button primary label={t('ticket.common.action.save')} onClick={onSubmit} disabled={createMode ? tagList.length < 1 : selectedTags.length < 1} />}
            cancelButton={<Button onClick={onClose} label={t(`ticket.common.action.cancel`)} />}
            title={t('eventList.tagModal.title')}
            subtitle={t('eventList.tagModal.subtitle')}
        >
            <div>
                {!createMode && tags.length > 0 ? (
                    <>
                        <Message type="default" title={t('eventList.tagModal.message-title')}>
                            <div>
                                <div>{t('eventList.tagModal.message-subtitle')}</div>
                                <div className={Style['create-tag-cta']} onClick={goCreateTags}>
                                    {t('eventList.tagModal.message-cta')}
                                </div>
                            </div>
                        </Message>
                        <SelectHierarchyLabel
                            multi
                            options={options}
                            selected={selected}
                            onChange={handleChange}
                            onLabelSelected={handleLabelChange}
                            label={t('eventList.tagModal.dropdown-label')}
                        />
                    </>
                ) : (
                    <>
                        <InputMulti items={tagList} onChange={value => setTagList(value)} />
                        <Message type={'tips'} title={t('ticket.common.tips')} mini>
                            <div>{t('eventList.tagModal.tips-description')}</div>
                        </Message>
                        {event?.tags.length > 0 && (
                            <div className={Style['back-to-select']} onClick={goBackToSelectTags}>
                                <Icon name="chevron-left" />
                                {t('eventList.tagModal.return')}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}
